/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState } from 'react';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import TruncateText from '../../shared/TruncatedText/TruncateText';
import {
  CarbonDetailMeterField,
  EnergyDetailMeterField,
  MAGIC_VALUE,
  UtilityType
} from '../Utils/Constants';
import CommonProgressBar from '../../shared/CommonProgressBar/CommonProgressBar';
import './CarbonDetailTabBreakdownByMeterTheme.scss';
import GasIcon from '../../../images/GasIcon.svg';
import ElectricityIcon from '../../../images/Energy-icon.svg';
import WaterIcon from '../../../images/WaterIcon.svg';
import TooltipError from '../../../images/tooltipError.svg';
import { ICarbonDetailTabBreakdownByMeterProps } from './ICarbonDetailTabBreakdownProps';
import {
  getConvertedUnit,
  getConvertedValues,
  getNullConverted,
  getNullToZero
} from '../Utils/EnergyUsagesViewDetailsUtils';
import { getPercentageColor } from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import { MeterUtilityType } from '../Enums';
import { renderTableColumns } from '../MeterSavingsDetailTable/MeterSavingsDetailTableUtility';
import { getConvertedValue } from '../Utils/unitHandling/unitHandling';
import { ConversionType } from '../Utils/unitHandling/UnitModel';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';

const conversionType: { [key: string]: string } = {
  Electric: ConversionType.ELECTRICITY,
  Water: ConversionType.WATER,
  Carbon: ConversionType.CARBON,
  Gas: ConversionType.GAS,
  Sewerage: ConversionType.SEWERAGE,
  All: ConversionType.ELECTRICITY
};

const CarbonDetailTabBreakdownByMeter = (
  props: ICarbonDetailTabBreakdownByMeterProps
) => {
  const {
    meterData,
    utilityMeasurementUnit,
    columnDetails,
    utilityType,
    isEnergy
  } = props;
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(
    isEnergy
      ? EnergyDetailMeterField.actualConsumption
      : CarbonDetailMeterField.actualConsumption
  );

  const getUtilityIcon = (type: string) => {
    switch (type) {
      case MeterUtilityType.WATER:
        return <img className="card-electricity-icon" src={WaterIcon} />;
      case MeterUtilityType.GAS:
        return <img className="card-electricity-icon" src={GasIcon} />;
      default:
        return <img className="card-electricity-icon" src={ElectricityIcon} />;
    }
  };

  const getUtilityBasedOnIcon = (cellData: ICellData, utilityType: string) => {
    const { value, field, rowData } = cellData;
    if (utilityType === UtilityType.All) {
      return (
        <span className="score-category-meterdata-meter ">
          <span className="score-category-Badge">
            {getUtilityIcon(rowData.utilityType)}
          </span>
          <span className="score-category-percent">
            <TruncateText text={value} maxLength={MAGIC_VALUE.THIRTY} />
          </span>
        </span>
      );
    } else {
      return (
        <span className="score-category-meterdata-meter ">
          <span className="score-category-Badge">
            {getUtilityIcon(utilityType)}
          </span>
          <span className="score-category-percent">
            <TruncateText text={value} maxLength={MAGIC_VALUE.THIRTY} />
          </span>
        </span>
      );
    }
  };

  const getCarbonChange = (value: any, rowData: any) => {
    const convertedValue: IAny = getConvertedValue(
      conversionType[
        props.sourceType ? props.sourceType : ConversionType.CARBON
      ],
      props.emissionMeasurementUnit ? props.emissionMeasurementUnit : '',
      value,
      rowData.changeUnit
    )?.Value;
    if (value === null) {
      return <>-</>;
    }
    return (
      <span className="score-category-meterdata-meter">
        <span
          className="savings-badge"
          style={{
            color: getPercentageColor(convertedValue)
          }}
        >
          {getConvertedValues(Math.abs(convertedValue))}
          {' ' +
            rowData.changeUnit +
            '(' +
            getConvertedValues(rowData.percentageConsumptionSaving) +
            '%' +
            ')'}
        </span>
      </span>
    );
  };

  const getEnergyChange = (convertedValue: any, rowData: any) => {
    if (convertedValue === null || convertedValue === undefined) {
      return <>--</>;
    }
    return (
      <span className="score-category-meterdata-meter">
        <span
          className="savings-badge"
          style={{
            color: getPercentageColor(rowData?.percentageConsumptionSaving)
          }}
        >
          {convertedValue}
          {' ' +
            rowData.changeUnit +
            '(' +
            getConvertedValues(rowData?.percentageConsumptionSaving) +
            '%' +
            ')'}
        </span>
      </span>
    );
  };

  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    switch (field) {
      case CarbonDetailMeterField.actualConsumption:
        return (
          <span className="emaas-carbondetail-category-meterdata">
            <span className="score-category-percent">
              <div className="measurementunit">
                {getNullConverted(
                  getConvertedValue(
                    conversionType[
                      props.sourceType
                        ? props.sourceType
                        : ConversionType.CARBON
                    ],
                    props.emissionMeasurementUnit
                      ? props.emissionMeasurementUnit
                      : '',
                    value,
                    rowData.actualUnit
                  )?.Value
                )}
              </div>
              {rowData.actualUnit}
            </span>
            <span className="score-category-progress">
              <CommonProgressBar
                actual={getNullToZero(rowData.actualConsumption)}
                previousPeriod={getNullToZero(rowData.baselineConsumption)}
                savings={rowData.change}
                savingsPercentage={getNullToZero(
                  rowData.percentageConsumptionSaving
                )}
                progessBarRelativeWidth={rowData.progessBarRelativeWidth}
              />
            </span>
          </span>
        );
      case CarbonDetailMeterField.utilityType:
        return (
          <span className="score-category-meterdata">
            <span className="score-category-percent">
              <div className="utilityType">{utilityType}</div>
            </span>
          </span>
        );
      case CarbonDetailMeterField.change:
        return getCarbonChange(value, rowData);

      case CarbonDetailMeterField.meter:
        return getUtilityBasedOnIcon(cellData, utilityType);
      case CarbonDetailMeterField.baselineConsumption:
        return (
          <span className="score-category-meterdata-meter">
            {value === null && (
              <img className="alarm-meter-icon" src={TooltipError} />
            )}
            <div className="consumptionvalue">
              {getNullConverted(
                getConvertedValue(
                  conversionType[
                    props.sourceType ? props.sourceType : ConversionType.CARBON
                  ],
                  props.emissionMeasurementUnit
                    ? props.emissionMeasurementUnit
                    : '',
                  value,
                  rowData.baselineUnit
                )?.Value
              )}
            </div>
            {rowData.baselineUnit}
          </span>
        );

      case EnergyDetailMeterField.actualConsumption:
        return (
          <span className="emaas-carbondetail-category-meterdata">
            <span className="score-category-percent">
              <div className="measurementunit">
                {getNullConverted(
                  getConvertedValue(
                    conversionType[
                      props.sourceType
                        ? props.sourceType
                        : ConversionType.CARBON
                    ],
                    props.emissionMeasurementUnit
                      ? props.emissionMeasurementUnit
                      : '',
                    value,
                    rowData.actualUnit
                  )?.Value
                )}
              </div>
              {getConvertedUnit(rowData.actualConsumption, rowData.actualUnit)}
            </span>
            <span className="score-category-progress">
              <CommonProgressBar
                actual={rowData.rawActualConsumption}
                previousPeriod={rowData.rawBaselineConsumption}
                savings={rowData.rawChange}
                savingsPercentage={rowData.percentageConsumptionSaving}
                progessBarRelativeWidth={rowData.progessBarRelativeWidth}
              />
            </span>
          </span>
        );
      case EnergyDetailMeterField.change:
        return getEnergyChange(
          getConvertedValue(
            conversionType[
              props.sourceType ? props.sourceType : ConversionType.CARBON
            ],
            props.emissionMeasurementUnit ? props.emissionMeasurementUnit : '',
            value === null ? null : Math.abs(value),
            rowData.changeUnit
          ).Value,
          rowData
        );

      case EnergyDetailMeterField.baselineConsumption:
        return (
          <span className="score-category-meterdata-meter">
            <div className="consumptionvalue">
              {getNullConverted(
                getConvertedValue(
                  conversionType[
                    props.sourceType ? props.sourceType : ConversionType.CARBON
                  ],
                  props.emissionMeasurementUnit
                    ? props.emissionMeasurementUnit
                    : '',
                  value,
                  rowData.baselineUnit
                )?.Value
              )}
            </div>
            {rowData.baselineUnit}
          </span>
        );
      default:
        return <></>;
    }
  };

  return (
    <DataTable
      className="Savingtable"
      data={meterData}
      onSort={({ sortField, sortOrder }) => {
        setFieldSortOrder(sortOrder as number);
        setSortFieldName(sortField);
      }}
      sortField={sortFieldName}
      sortOrder={fieldSortOrder}
      scrollable={true}
      scrollHeight="20rem"
    >
      {renderTableColumns(columnDetails, cellDataRenderer)}
    </DataTable>
  );
};
export default CarbonDetailTabBreakdownByMeter;
